import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CheckIcon } from '@heroicons/react/24/outline';
import { searchUsAddresses } from '../../utils/usAddressSearch';
import { dropdownPanelVariantsForPlacement, dropdownTransition } from './dropdownMotion';
import { DROPDOWN_Z_INDEX, useFloatingDropdownPosition } from '../../hooks/useFloatingPanelPosition';

function UsAddressAutocomplete({ value, onChange, className, placeholder, id, searchingLabel = 'Searching addresses…' }) {
  const generatedId = useId();
  const inputId = id || generatedId;
  const listboxId = `${inputId}-suggestions`;

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const panelPosition = useFloatingDropdownPosition({
    open,
    anchorRef: inputRef,
    panelRef: dropdownRef,
    itemCount: suggestions.length,
  });

  const placement = panelPosition?.placement ?? 'bottom';
  const panelVariants = dropdownPanelVariantsForPlacement(placement);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      const inInput = rootRef.current?.contains(event.target);
      const inDropdown = dropdownRef.current?.contains(event.target);
      if (!inInput && !inDropdown) {
        setOpen(false);
        setActiveIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function scheduleSearch(query) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      setActiveIndex(-1);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;

      searchUsAddresses(trimmed, { signal: controller.signal })
        .then((results) => {
          setSuggestions(results);
          setOpen(results.length > 0);
          setActiveIndex(-1);
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            setSuggestions([]);
            setOpen(false);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 300);
  }

  function selectSuggestion(label) {
    onChange(label);
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);
  }

  function handleInputChange(event) {
    const nextValue = event.target.value;
    onChange(nextValue);
    scheduleSearch(nextValue);
  }

  function handleKeyDown(event) {
    if (!open || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[activeIndex].label);
    } else if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  const reducedMotion = useReducedMotion();
  const transition = dropdownTransition(reducedMotion);

  const dropdown =
    typeof document !== 'undefined'
      ? createPortal(
          <AnimatePresence>
            {open && panelPosition ? (
              <motion.ul
                ref={dropdownRef}
                id={listboxId}
                role="listbox"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={panelVariants}
                transition={transition}
                style={{
                  position: 'fixed',
                  top: panelPosition.top,
                  left: panelPosition.left,
                  width: panelPosition.width,
                  maxHeight: panelPosition.maxHeight,
                  zIndex: DROPDOWN_Z_INDEX,
                }}
                className={`dropdown-panel-ios overflow-y-auto p-1.5 ${
                  placement === 'top' ? 'origin-bottom' : 'origin-top'
                }`}
              >
                {suggestions.map((item, index) => (
                  <motion.li
                    key={item.id}
                    role="option"
                    aria-selected={index === activeIndex}
                    initial={reducedMotion ? false : { opacity: 0, y: placement === 'top' ? -6 : 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={
                      reducedMotion
                        ? { duration: 0.01 }
                        : { type: 'spring', stiffness: 420, damping: 32, delay: index * 0.035 }
                    }
                  >
                    <button
                      type="button"
                      className={`dropdown-option-ios ${
                        index === activeIndex
                          ? 'dropdown-option-ios-active dropdown-option-ios-selected'
                          : ''
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => selectSuggestion(item.label)}
                    >
                      <span className="truncate">{item.label}</span>
                      {index === activeIndex ? (
                        <CheckIcon className="dropdown-option-check h-4 w-4 shrink-0" aria-hidden="true" />
                      ) : null}
                    </button>
                  </motion.li>
                ))}
              </motion.ul>
            ) : null}
          </AnimatePresence>,
          document.body
        )
      : null;

  return (
    <div ref={rootRef} className="relative">
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        autoComplete="street-address"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        className={className}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
      />

      {dropdown}

      {loading && value.trim().length >= 3 ? (
        <p className="mt-1 text-xs text-secondary-500 ">{searchingLabel}</p>
      ) : null}
    </div>
  );
}

export default UsAddressAutocomplete;
